/**
 * TOUR-02 — Diff entre les conteneurs du patrimoine (P) et les lignes du fichier (F).
 *
 * Fonction pure. Spec : cadrage TOUR-02 §4 (R3, R7–R14, R16).
 *
 * @typedef {Object} ConteneurLike   (sous-ensemble du groupe `patrimoine:detail`)
 * @property {number} [id]
 * @property {string} nomenclature
 * @property {string} [adresse]
 * @property {string|number|null} [latitude]
 * @property {string|number|null} [longitude]
 * @property {number|null} [ordreCsv]
 *
 * @typedef {Object} DiffEntry
 * @property {import('./parseTourneeCsv').TourneeCsvRow} row
 * @property {ConteneurLike|null} conteneur      null si inconnu (R14)
 * @property {'unknown'|'unchanged'|'changed'} status
 * @property {{from: number|null, to: number}|null} ordre
 * @property {{from: string, to: string}|null} adresse
 * @property {{from: {lat: number, lng: number}|null, to: {lat: number, lng: number}, distanceM: number|null}|null} coords
 *
 * @typedef {Object} AbsentEntry
 * @property {ConteneurLike} conteneur
 * @property {boolean} ordreCollision   son ordreCsv est repris par une ligne du fichier (R13)
 * @property {string|null} collisionWith nomenclature en conflit
 *
 * @typedef {Object} DiffResult
 * @property {DiffEntry[]} entries      dans l'ordre du fichier
 * @property {AbsentEntry[]} absents    conteneurs de P absents de F (R13), triés par ordreCsv
 * @property {DiffEntry[]} unknown      lignes de F sans correspondance (R14)
 * @property {{ordre: number, adresse: number, coords: number, changed: number, absents: number, unknown: number}} counts
 * @property {boolean} hasChanges       R16
 * @property {boolean} isReconciled     R13/R14 (amendement 2026-09-02) : aucun absent, aucun inconnu —
 *                                      le fichier est une permutation exacte du patrimoine, condition
 *                                      d'application (R15)
 */

const EARTH_RADIUS_M = 6371000;

/** Distance Haversine en mètres (même formule que helper.getDistanceMeters, sans dépendance). */
export function haversineMeters(lat1, lng1, lat2, lng2) {
    const toRad = (v) => (Number(v) * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Coordonnée base → nombre, ou null si absente/non numérique. */
function toCoord(value) {
    if (value === null || value === undefined) return null;
    const s = String(value).trim();
    if (s === '') return null;
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
}

/**
 * @param {ConteneurLike[]} existing conteneurs du patrimoine
 * @param {import('./parseTourneeCsv').TourneeCsvRow[]} rows lignes valides du fichier
 * @returns {DiffResult}
 */
export function diffConteneurs(existing, rows) {
    const byNomenclature = new Map();
    (existing ?? []).forEach((c) => {
        // R3 : clé exacte, aucune normalisation
        if (c && typeof c.nomenclature === 'string') byNomenclature.set(c.nomenclature, c);
    });

    const seen = new Set();
    const entries = (rows ?? []).map((row) => {
        const conteneur = byNomenclature.get(row.nomenclature) ?? null;
        if (!conteneur) {
            return {row, conteneur: null, status: 'unknown', ordre: null, adresse: null, coords: null};
        }
        seen.add(row.nomenclature);

        // R7 — ordre
        const currentOrdre = conteneur.ordreCsv ?? null;
        const ordre = currentOrdre !== row.ordreCsv ? {from: currentOrdre, to: row.ordreCsv} : null;

        // R8 — adresse (trim des deux côtés, vide dans F = non comparée)
        let adresse = null;
        if (row.adresse !== '') {
            const current = String(conteneur.adresse ?? '').trim();
            if (current !== row.adresse) adresse = {from: current, to: row.adresse};
        }

        // R9, R11, R12 — coords (égalité numérique, sans seuil)
        let coords = null;
        if (row.latitude !== null && row.longitude !== null) {
            const toLat = Number(row.latitude);
            const toLng = Number(row.longitude);
            const fromLat = toCoord(conteneur.latitude);
            const fromLng = toCoord(conteneur.longitude);
            const hasFrom = fromLat !== null && fromLng !== null;
            if (!hasFrom) {
                coords = {from: null, to: {lat: toLat, lng: toLng}, distanceM: null};
            } else if (fromLat !== toLat || fromLng !== toLng) {
                coords = {
                    from: {lat: fromLat, lng: fromLng},
                    to: {lat: toLat, lng: toLng},
                    distanceM: Math.round(haversineMeters(fromLat, fromLng, toLat, toLng)),
                };
            }
        }

        const status = ordre || adresse || coords ? 'changed' : 'unchanged';
        return {row, conteneur, status, ordre, adresse, coords};
    });

    // R13 — absents du fichier, jamais supprimés ; collision d'ordre signalée
    const newOrdres = new Map();
    entries.forEach((e) => {
        if (e.conteneur) newOrdres.set(e.row.ordreCsv, e.row.nomenclature);
    });
    const absents = (existing ?? [])
        .filter((c) => c && !seen.has(c.nomenclature))
        .map((c) => {
            const collisionWith = c.ordreCsv != null ? (newOrdres.get(c.ordreCsv) ?? null) : null;
            return {conteneur: c, ordreCollision: collisionWith !== null, collisionWith};
        })
        .sort((a, b) => (a.conteneur.ordreCsv ?? 0) - (b.conteneur.ordreCsv ?? 0));

    const unknown = entries.filter((e) => e.status === 'unknown');
    const changed = entries.filter((e) => e.status === 'changed');
    const counts = {
        ordre: changed.filter((e) => e.ordre).length,
        adresse: changed.filter((e) => e.adresse).length,
        coords: changed.filter((e) => e.coords).length,
        changed: changed.length,
        absents: absents.length,
        unknown: unknown.length,
    };

    return {
        entries, absents, unknown, counts,
        hasChanges: changed.length > 0,
        isReconciled: absents.length === 0 && unknown.length === 0,
    };
}

/**
 * Payload de `POST /api/patrimoines/{id}/plan-tournee` : uniquement les conteneurs
 * modifiés, uniquement les champs modifiés (+ nomenclature et ordreCsv, obligatoires).
 * @param {DiffResult} diff
 * @returns {{conteneurs: Array<{nomenclature: string, ordreCsv: number, adresse?: string, latitude?: string, longitude?: string}>}}
 */
export function buildPlanTourneePayload(diff) {
    const conteneurs = (diff?.entries ?? [])
        .filter((e) => e.status === 'changed')
        .map((e) => {
            const item = {nomenclature: e.row.nomenclature, ordreCsv: e.row.ordreCsv};
            if (e.adresse) item.adresse = e.adresse.to;
            if (e.coords) {
                item.latitude = e.row.latitude;
                item.longitude = e.row.longitude;
            }
            return item;
        });
    return {conteneurs};
}

export default diffConteneurs;
